import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, endOfMonth, format, parseISO, subMonths } from "date-fns";
import { fr } from "date-fns/locale";

export const useExpenses = () => {
  const { data: expensesData, refetch } = useQuery({
    queryKey: ["expenses_summary"],
    queryFn: async () => {
      const startDate = startOfMonth(new Date());
      const endDate = endOfMonth(new Date());
      const previousMonthStart = startOfMonth(subMonths(new Date(), 1));
      const previousMonthEnd = endOfMonth(subMonths(new Date(), 1));

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data: currentExpenses, error: currentError } = await supabase
        .from('poultry_transactions')
        .select('*')
        .eq('transaction_type', 'expense')
        .eq('created_by', user.id)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (currentError) throw currentError;

      const { data: previousExpenses, error: previousError } = await supabase
        .from('poultry_transactions')
        .select('*')
        .eq('transaction_type', 'expense')
        .eq('created_by', user.id)
        .gte('created_at', previousMonthStart.toISOString())
        .lte('created_at', previousMonthEnd.toISOString());

      if (previousError) throw previousError;

      // Calcul des statistiques
      const totalExpenses = currentExpenses.reduce((sum, expense) => sum + expense.total_amount, 0);
      const previousTotal = previousExpenses.reduce((sum, expense) => sum + expense.total_amount, 0);
      const monthlyChange = previousTotal ? ((totalExpenses - previousTotal) / previousTotal) * 100 : 0;

      // Calcul des dépenses quotidiennes
      const dailyExpenses = currentExpenses.reduce((acc, expense) => {
        const date = format(parseISO(expense.created_at), 'dd/MM', { locale: fr });
        acc[date] = (acc[date] || 0) + expense.total_amount;
        return acc;
      }, {});

      const dailyData = Object.entries(dailyExpenses).map(([date, amount]) => ({
        date,
        amount: Number(amount)
      }));

      // Calcul des dépenses par catégorie
      const categoryExpenses = currentExpenses.reduce((acc, expense) => {
        const category = expense.notes || 'Non catégorisé';
        acc[category] = (acc[category] || 0) + expense.total_amount;
        return acc;
      }, {});

      const categoryData = Object.entries(categoryExpenses).map(([name, value]) => ({
        name,
        value: Number(value)
      }));

      // Calcul des statistiques supplémentaires
      const daysWithExpenses = Object.keys(dailyExpenses).length;
      const averageDaily = daysWithExpenses ? totalExpenses / daysWithExpenses : 0;
      const expenseCount = currentExpenses.length;
      const categoryCount = Object.keys(categoryExpenses).length;

      return {
        dailyData,
        categoryData,
        stats: {
          totalExpenses,
          monthlyChange,
          averageDaily,
          daysWithExpenses,
          expenseCount,
          categoryCount
        }
      };
    },
  });

  // Écoute des changements en temps réel
  useEffect(() => {
    const channel = supabase
      .channel('expenses-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'poultry_transactions',
          filter: 'transaction_type=eq.expense'
        },
        () => {
          console.log('Expense change detected, refetching data...');
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  return expensesData;
};